import { rollD6 } from '../utils/dice.js';
import { createImmatureWorker } from './workerSystem.js';
import { CONFIG } from '../config/constants.js';

/**
 * Applies immediate event effects
 * @param {Object} event - The event object
 * @param {Object} gameState - Current game state
 * @returns {Object} Updated game state
 */
export function applyEventEffect(event, gameState) {
  if (!event || event.type === 'quiet') {
    return gameState;
  }

  const handler = EVENT_HANDLERS[event.type];
  if (!handler) {
    return gameState;
  }

  return handler(event, gameState);
}

// Event handlers
const EVENT_HANDLERS = {
  spoilage: (event, state) => {
    const hasGranary = state.buildings.some(b => b.type === 'granary' && b.complete);
    const spoilAmount = hasGranary ? Math.ceil(rollD6() / 2) : rollD6();

    return {
      ...state,
      food: Math.max(0, state.food - spoilAmount),
      message: `Lost ${spoilAmount} food to spoilage.`
    };
  },

  warning: (event, state) => {
    return {
      ...state,
      raidScheduledForDay: state.day + 1, // Raid happens NEXT day
      raidWarning: true // Keep for compatibility
    };
  },

  raid: (event, state) => {
    // Raid happens at end of THIS day (immediate)
    return {
      ...state,
      raidScheduledForDay: state.day, // Raid happens THIS day
      raidWarning: true // Keep for compatibility
    };
  },

  storm: (event, state) => state,
  weather: (event, state) => state,
  bounty: (event, state) => state,
  quiet: (event, state) => state,

  // Refugees is a choice event (welcome / turn away); resolved in handleStoryChoice,
  // so applyEventEffect is never invoked for it — no handler needed here.

  discovery: (event, state) => {
    const metalFound = rollD6();
    return {
      ...state,
      metal: (state.metal || 0) + metalFound,
      message: `Found ${metalFound} metal!`
    };
  },

  festival: (event, state) => {
    // +15 morale. Phase skip (no work today) handled in handleContinueFromEvent.
    return {
      ...state,
      morale: Math.min(100, (state.morale || 50) + 15),
      message: 'Your people celebrate! No work today, but spirits are high.'
    };
  },

  scholar: (event, state) => {
    const knowledgeGained = rollD6() + rollD6();
    return {
      ...state,
      knowledge: Math.min(CONFIG.KNOWLEDGE_CAP, (state.knowledge || 0) + knowledgeGained),
      message: `Scholar shared ${knowledgeGained} knowledge!`
    };
  },

  merchant: (event, state) => {
    // Merchant is a choice event; applyEventEffect is not called for choice events.
    // This handler is here for completeness but won't be invoked.
    return state;
  },

  breakthrough: (event, state) => {
    const hasWorkshop = state.buildings.some(b => b.type === 'workshop' && b.complete);
    if (!hasWorkshop) {
      // Fallback: apply discovery effect instead
      const metalFound = rollD6();
      return {
        ...state,
        metal: (state.metal || 0) + metalFound,
        message: `No workshop for the breakthrough — but your settlers found ${metalFound} metal!`
      };
    }

    const techGained = rollD6();
    return {
      ...state,
      techPoints: (state.techPoints || 0) + techGained,
      message: `Research breakthrough! +${techGained} tech points.`
    };
  },

  golden_age: (event, state) => {
    // +3 to all tasks is handled in calculateTaskTotal via event?.type === 'golden_age'
    return {
      ...state,
      morale: Math.min(100, (state.morale || 50) + 15),
      message: 'A golden age dawns! All tasks receive +3 today.'
    };
  },

  traveler_choice: (event, state) => {
    // Don't auto-apply temp worker, let choice handler decide
    return state;
  }
};

/**
 * Checks if a birth occurs this day
 * @param {Object} gameState - Current game state
 * @returns {Object|null} Birth event object or null
 */
export function checkBirth(gameState) {
  const aliveWorkers = gameState.workers.filter(w => w.status !== 'dead');

  // Need minimum population for births
  if (aliveWorkers.length < 5) return null;

  // Calculate birth chance based on buildings
  const hasNursery = gameState.buildings.some(b => b.type === 'nursery' && b.complete);
  const birthChance = hasNursery ? 30 : 10; // 10% base, +20% with nursery

  // Roll 1d100 for birth check
  const roll = Math.floor(Math.random() * 100) + 1;

  if (roll <= birthChance) {
    const maturityDays = hasNursery ? 3 : 5;
    const maxId = Math.max(...gameState.workers.map(w => typeof w.id === 'number' ? w.id : 0));
    const newWorker = createImmatureWorker(maxId + 1, gameState.day, maturityDays);

    return {
      type: 'birth',
      name: 'Birth',
      icon: '👶',
      message: `A child was born! They will mature in ${maturityDays} days.`,
      worker: newWorker,
      moraleBonus: 10
    };
  }

  return null;
}
